import { useRef, useMemo, useEffect, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Stars, Html } from '@react-three/drei'
import * as THREE from 'three'
import type { ChartData, ZodiacSign } from '../types'

interface Chart3DProps {
  chartData: ChartData
}

// 星座颜色 - 赛博朋克风格
const zodiacColors: Record<ZodiacSign, string> = {
  Aries: '#ff4d6d',      // 火 - 霓虹红
  Leo: '#ff9500',        // 火 - 霓虹橙
  Sagittarius: '#ffd60a', // 火 - 霓虹黄
  Taurus: '#06ffa5',     // 土 - 赛博绿
  Virgo: '#00d26a',      // 土 - 翠绿
  Capricorn: '#2d6a4f',  // 土 - 深绿
  Gemini: '#00f0ff',     // 风 - 赛博蓝
  Libra: '#90e0ef',      // 风 - 浅蓝
  Aquarius: '#9d00ff',   // 风 - 赛博紫
  Cancer: '#0077b6',     // 水 - 深蓝
  Scorpio: '#023e8a',    // 水 - 深海蓝
  Pisces: '#c77dff',     // 水 - 紫罗兰
}

// 行星颜色 - 发光效果
const planetColors: Record<string, string> = {
  sun: '#ffd700',    // 金色
  moon: '#e0e0e0',   // 银白
  mercury: '#a8dadc', // 水银
  venus: '#ffb3c6',   // 粉红
  mars: '#ff4d6d',    // 红
  jupiter: '#c9ada7', // 棕金
  saturn: '#f4d03f',  // 金黄
  uranus: '#48cae4',  // 天蓝
  neptune: '#4361ee', // 深海蓝
  pluto: '#6d6875',   // 暗灰
}

// 行星符号
const planetSymbols: Record<string, string> = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
  jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
}

// 星座符号
const zodiacSymbols: Record<string, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
}

// 发光材质组件
function GlowMesh({ color, size, intensity = 1 }: { color: string; size: number; intensity?: number }) {
  return (
    <mesh scale={size}>
      <sphereGeometry args={[1, 32, 32]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={intensity}
        toneMapped={false}
      />
    </mesh>
  )
}

// 发光环
function GlowRing({ radius, color, opacity = 0.3 }: { radius: number; color: string; opacity?: number }) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]}>
      <torusGeometry args={[radius, 0.02, 16, 128]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} />
    </mesh>
  )
}

// 3D 星盘场景
function Scene({ chartData }: { chartData: ChartData }) {
  const groupRef = useRef<THREE.Group>(null)
  const [hoveredPlanet, setHoveredPlanet] = useState<string | null>(null)

  // 自动缓慢旋转
  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.1
    }
  })

  // 宫位环 - 12个宫位
  const houseRings = useMemo(() => {
    const rings: JSX.Element[] = []
    const outerRadius = 5
    const innerRadius = 3

    for (let i = 0; i < 12; i++) {
      const angle = (i * 30 * Math.PI) / 180
      const nextAngle = ((i + 1) * 30 * Math.PI) / 180

      const startX = Math.cos(angle) * outerRadius
      const startZ = Math.sin(angle) * outerRadius
      const endX = Math.cos(nextAngle) * outerRadius
      const endZ = Math.sin(nextAngle) * outerRadius

      const midAngle = angle + 15 * Math.PI / 180
      const labelRadius = (outerRadius + innerRadius) / 2
      const labelX = Math.cos(midAngle) * labelRadius
      const labelZ = Math.sin(midAngle) * labelRadius

      const sign = Object.keys(zodiacSymbols)[i] as ZodiacSign
      const color = zodiacColors[sign]
      const house = chartData.houses.find(h => h.number === i + 1)
      const houseSign = house?.sign as ZodiacSign
      const houseColor = houseSign ? zodiacColors[houseSign] : color

      rings.push(
        <group key={`house-${i}`}>
          {/* 宫位边界线 */}
          <line>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array([startX, 0, startZ, endX, 0, endZ])}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial color={houseColor} opacity={0.6} transparent linewidth={2} />
          </line>
          
          {/* 宫位分割线到中心 */}
          <line>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array([startX, 0, startZ, 0, 0, 0])}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial color={houseColor} opacity={0.2} transparent />
          </line>

          {/* 外圈弧 - 带发光效果 */}
          <mesh rotation={[-Math.PI / 2, 0, angle]} position={[0, 0.01, 0]}>
            <torusGeometry args={[outerRadius, 0.05, 8, 32, Math.PI / 6]} />
            <meshBasicMaterial color={houseColor} opacity={0.8} transparent />
          </mesh>

          {/* 星座符号标签 */}
          <Html position={[labelX, 0.02, labelZ]} center>
            <div className="text-xl select-none" style={{ color: houseColor, textShadow: `0 0 10px ${houseColor}` }}>
              {zodiacSymbols[sign]}
            </div>
          </Html>

          {/* 宫位数字 */}
          <Html position={[Math.cos(midAngle) * (outerRadius - 0.4), 0.02, Math.sin(midAngle) * (outerRadius - 0.4)]} center>
            <div className="text-xs font-mono text-gray-400">{i + 1}</div>
          </Html>
        </group>
      )
    }
    return rings
  }, [chartData.houses])

  // 行星位置
  const planets = useMemo(() => {
    return Object.entries(chartData.planets).map(([name, planet]) => {
      const angle = ((planet.longitude - 180) * Math.PI) / 180 // 调整角度使上升点在左边
      const radius = 3.5
      const x = Math.cos(angle) * radius
      const z = Math.sin(angle) * radius
      const y = 0.1

      const isHovered = hoveredPlanet === name
      const size = isHovered ? 0.25 : 0.18

      return (
        <group key={name} position={[x, y, z]}>
          {/* 行星轨道环（细线） */}
          <mesh rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[radius - 0.1, radius + 0.1, 64]} />
            <meshBasicMaterial color={planetColors[name]} opacity={0.05} transparent />
          </mesh>

          {/* 行星本体 - 发光球体 */}
          <group
            onPointerOver={() => setHoveredPlanet(name)}
            onPointerOut={() => setHoveredPlanet(null)}
            scale={isHovered ? 1.3 : 1}
          >
            {/* 外发光 */}
            <mesh scale={size * 2}>
              <sphereGeometry args={[1, 16, 16]} />
              <meshBasicMaterial
                color={planetColors[name]}
                transparent
                opacity={0.2}
              />
            </mesh>
            
            {/* 行星本体 */}
            <GlowMesh color={planetColors[name]} size={size} intensity={isHovered ? 2 : 1} />
            
            {/* 行星符号 */}
            <Html center distanceFactor={10}>
              <div 
                className={`text-lg font-bold transition-all duration-300 ${isHovered ? 'scale-125' : ''}`}
                style={{ 
                  color: planetColors[name],
                  textShadow: `0 0 ${isHovered ? '15px' : '8px'} ${planetColors[name]}`,
                }}
              >
                {planetSymbols[name]}
              </div>
            </Html>
          </group>

          {/* 悬停提示 */}
          {isHovered && (
            <Html position={[0.4, 0.3, 0]}>
              <div className="bg-cyber-dark/90 border border-cyber-blue/30 rounded-lg px-3 py-2 text-xs whitespace-nowrap">
                <div className="text-white font-medium capitalize">{name}</div>
                <div className="text-cyber-blue">{planet.sign} {planet.degree.toFixed(1)}°</div>
                <div className="text-gray-400">第{planet.house}宫</div>
              </div>
            </Html>
          )}
        </group>
      )
    })
  }, [chartData.planets, hoveredPlanet])

  // 相位线 - 赛博朋克发光线条
  const aspectLines = useMemo(() => {
    return chartData.aspects.slice(0, 12).map((aspect, index) => {
      const p1 = chartData.planets[aspect.planet1]
      const p2 = chartData.planets[aspect.planet2]

      if (!p1 || !p2) return null

      const angle1 = ((p1.longitude - 180) * Math.PI) / 180
      const angle2 = ((p2.longitude - 180) * Math.PI) / 180

      const radius = 3.5
      const x1 = Math.cos(angle1) * radius
      const z1 = Math.sin(angle1) * radius
      const x2 = Math.cos(angle2) * radius
      const z2 = Math.sin(angle2) * radius

      // 相位颜色和样式
      let color = '#ffffff'
      let opacity = 0.15
      switch (aspect.aspect) {
        case 'conjunction':
          color = '#00f0ff' // 青色 - 合相
          opacity = 0.6
          break
        case 'trine':
          color = '#00ff9d' // 绿色 - 三分相
          opacity = 0.4
          break
        case 'square':
          color = '#ff00a0' // 粉色 - 四分相
          opacity = 0.4
          break
        case 'opposition':
          color = '#ff4d6d' // 红色 - 对分相
          opacity = 0.4
          break
        case 'sextile':
          color = '#9d00ff' // 紫色 - 六分相
          opacity = 0.3
          break
      }

      return (
        <line key={`aspect-${index}`}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={2}
              array={new Float32Array([x1, 0.05, z1, x2, 0.05, z2])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color={color} opacity={opacity} transparent />
        </line>
      )
    }).filter(Boolean)
  }, [chartData])

  // 上升点标记
  const ascendantMarker = useMemo(() => {
    const asc = chartData.ascendant
    const angle = ((asc.longitude - 180) * Math.PI) / 180
    const radius = 5.2
    const x = Math.cos(angle) * radius
    const z = Math.sin(angle) * radius

    return (
      <group position={[x, 0.1, z]}>
        {/* 金色箭头 */}
        <mesh rotation={[0, -angle + Math.PI / 2, 0]}>
          <coneGeometry args={[0.15, 0.4, 8]} />
          <meshStandardMaterial color="#ffd700" emissive="#ffd700" emissiveIntensity={0.5} />
        </mesh>
        {/* 文字标签 */}
        <Html position={[0, 0.3, 0]} center>
          <div className="text-xs font-mono text-amber-400 bg-cyber-dark/80 px-2 py-1 rounded">
            ASC {asc.degree.toFixed(0)}°
          </div>
        </Html>
      </group>
    )
  }, [chartData.ascendant])

  return (
    <group ref={groupRef}>
      {/* 背景星空 */}
      <Stars radius={100} depth={50} count={2000} factor={4} saturation={0.5} fade speed={0.5} />

      {/* 环境光 */}
      <ambientLight intensity={0.3} />
      <pointLight position={[10, 10, 10]} intensity={0.8} color="#00f0ff" />
      <pointLight position={[-10, -10, -10]} intensity={0.5} color="#ff00a0" />

      {/* 中心发光球 - 地球 */}
      <mesh>
        <sphereGeometry args={[0.25, 32, 32]} />
        <meshStandardMaterial color="#1a1a2e" emissive="#00f0ff" emissiveIntensity={0.2} />
      </mesh>

      {/* 内圈发光环 */}
      <GlowRing radius={2.5} color="#00f0ff" opacity={0.2} />
      
      {/* 外圈发光环 */}
      <GlowRing radius={5} color="#9d00ff" opacity={0.3} />

      {/* 宫位 */}
      {houseRings}

      {/* 行星 */}
      {planets}

      {/* 相位线 */}
      {aspectLines}

      {/* 上升点 */}
      {ascendantMarker}

      {/* 天顶标记 */}
      {chartData.midheaven && (
        <Html 
          position={[0, 0.1, -5.3]} 
          center
        >
          <div className="text-xs font-mono text-cyber-purple bg-cyber-dark/80 px-2 py-1 rounded">
            MC {chartData.midheaven.degree.toFixed(0)}°
          </div>
        </Html>
      )}
    </group>
  )
}

// 加载组件
function LoadingScene() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <div className="cyber-loader mb-4" />
      <p className="text-cyber-blue font-cyber text-sm animate-pulse">星盘加载中...</p>
    </div>
  )
}

export default function Chart3D({ chartData }: Chart3DProps) {
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800)
    return () => clearTimeout(timer)
  }, [])

  if (isLoading || !chartData) {
    return (
      <div className="w-full h-[500px] cyber-card flex items-center justify-center">
        <LoadingScene />
      </div>
    )
  }

  return (
    <div className="relative w-full h-[500px] cyber-card overflow-hidden">
      <Canvas camera={{ position: [0, 10, 10], fov: 35 }}>
        <Scene chartData={chartData} />
        <OrbitControls
          enablePan={false}
          enableZoom={true}
          minDistance={6}
          maxDistance={20}
          autoRotate={false}
        />
      </Canvas>

      {/* 控制提示 */}
      <div className="absolute bottom-4 left-4 flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-4 h-4 rounded bg-cyber-dark border border-cyber-blue/30 flex items-center justify-center">🖱</span>
          拖拽旋转
        </span>
        <span className="flex items-center gap-1">
          <span className="w-4 h-4 rounded bg-cyber-dark border border-cyber-blue/30 flex items-center justify-center">🔍</span>
          滚轮缩放
        </span>
        <span className="flex items-center gap-1">
          <span className="w-4 h-4 rounded bg-cyber-dark border border-cyber-blue/30 flex items-center justify-center">👆</span>
          悬停查看
        </span>
      </div>

      {/* 图例 */}
      <div className="absolute top-4 right-4 text-xs space-y-2 bg-cyber-dark/80 backdrop-blur-sm rounded-lg p-3 border border-cyber-blue/10">
        <div className="text-gray-400 font-cyber mb-2">相位图例</div>
        {[
          { color: '#00f0ff', label: '合相' },
          { color: '#00ff9d', label: '三分相' },
          { color: '#ff00a0', label: '四分相' },
          { color: '#ff4d6d', label: '对分相' },
          { color: '#9d00ff', label: '六分相' },
        ].map(item => (
          <div key={item.label} className="flex items-center gap-2">
            <span className="w-6 h-0.5" style={{ backgroundColor: item.color }} />
            <span className="text-gray-400">{item.label}</span>
          </div>
        ))}
      </div>

      {/* 切换2D提示 */}
      <div className="absolute top-4 left-4">
        <span className="px-3 py-1 rounded-full bg-cyber-blue/10 text-cyber-blue text-xs border border-cyber-blue/20">
          3D 视图
        </span>
      </div>
    </div>
  )
}
